import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11 } from "styled-components";
import * as React from "react";
import { type Mode } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { RichText } from "~/features/ui/rich-text";
import { takeFirst } from "~/features/app/utils";
import { Headline } from "~/features/ui/headline";
import { Icon } from "~/features/ui/icon";
import Loading from "~/public/icons/loading.svg";
import type { MarketoForms2 } from "./types";
import { validateField } from "./validation";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
const variants = {
  general: {
    "paddingLeft": "var(--spacing-md)",
    "paddingRight": "var(--spacing-md)",
    "paddingTop": "var(--spacing-lg)",
    "paddingBottom": "var(--spacing-lg)",
    "@media (min-width: 1024px)": {
      "paddingLeft": "var(--spacing-3xl)",
      "paddingRight": "var(--spacing-3xl)",
      "paddingTop": "var(--spacing-xl)",
      "paddingBottom": "var(--spacing-xl)"
    }
  }
} as const;
const cleanCssString = (css: string) => {
  const decoded = new DOMParser().parseFromString(css, "text/html").documentElement.textContent || "";
  return decoded.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
};
export type MarketoEmbedFormBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  customCss?: string | null;
  description?: string | null;
  fullWidth?: boolean | null;
  hasRedirect?: boolean | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  marketoFormId?: string | null;
  marketoFormInstanceId?: string | null;
  marketoFormUrl?: string | null;
  mode?: string | null;
  redirectUrl?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  successMessageDescription?: React.ReactNode;
  successMessageImage?: React.ReactNode;
  successMessageTitle?: string | null;
};
export function MarketoEmbedFormBlockView({
  anchor,
  customCss,
  description,
  fullWidth = false,
  hasRedirect,
  hasTopDivider = true,
  headline,
  marketoFormId,
  marketoFormInstanceId,
  marketoFormUrl,
  mode = "light",
  redirectUrl,
  sectionProps,
  successMessageDescription,
  successMessageImage,
  successMessageTitle
}: MarketoEmbedFormBlockViewProps) {
  const scriptPromiseRef = React.useRef<Promise<void> | null>(null);
  const formInitializedRef = React.useRef(false);
  const styleRef = React.useRef<HTMLStyleElement | null>(null);
  const eventHandlersRef = React.useRef<(() => void)[]>([]);
  const formContainerId = `mktoForm_${marketoFormId}`;
  const [isFormReady, setIsFormReady] = React.useState(false);
  const loadMktoScript = React.useCallback(() => {
    const scriptUrl = `${marketoFormUrl}/js/forms2/js/forms2.min.js`;
    // skip if already loaded globally or via script tag
    if (window.MktoForms2) return Promise.resolve();
    if (scriptPromiseRef.current) return scriptPromiseRef.current;
    // avoid duplicate tags
    if (!document.querySelector(`script[src="${scriptUrl}"]`)) {
      scriptPromiseRef.current = new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = scriptUrl;
        s.onload = () => resolve();
        s.onerror = e => reject(e);
        document.head.appendChild(s);
      });
    } else {
      // script tag exists but MktoForms2 may not be attached yet
      scriptPromiseRef.current = new Promise(resolve => {
        const check = () => {
          if ((window as any).MktoForms2) resolve();else setTimeout(check, 50);
        };
        check();
      });
    }
    return scriptPromiseRef.current;
  }, [marketoFormUrl]);
  React.useEffect(() => {
    if (!marketoFormUrl || !marketoFormInstanceId || !marketoFormId) return;
    if (formInitializedRef.current) return;
    let mounted = true;
    loadMktoScript().then(() => {
      if (!mounted || formInitializedRef.current || !window.MktoForms2) return;
      formInitializedRef.current = true;
      (window.MktoForms2 as MarketoForms2).loadForm(marketoFormUrl, marketoFormInstanceId, marketoFormId, form => {
        form.setShowTooltip?.(false);
        form.submittable(true);
        const container = document.getElementById(formContainerId);
        if (container && fullWidth) container.classList.add("form-full-width");
        const formElems = form.getFormElem();
        const native = formElems?.[0] as HTMLElement;
        if (!native) return;
        if (mounted) setIsFormReady(true);

        // Handle phone label row styling
        const phoneLabel = native.querySelector(".mktoFormRow .mktoHtmlText strong");
        if (phoneLabel?.textContent?.includes("Phone Number:")) {
          const labelRow = phoneLabel.closest(".mktoFormRow");
          const nextRow = labelRow?.nextElementSibling as HTMLElement;
          if (labelRow && nextRow) {
            // Add a custom class for styling
            labelRow.querySelector(".mktoFormCol")?.classList.add("phone-label-margin");
            // Hide gutters in the next row (phone inputs)
            nextRow.querySelectorAll(".mktoGutter").forEach(gutter => {
              (gutter as HTMLElement).style.display = "none";
            });
          }
        }
        ["blur", "change", "input"].forEach(evt => {
          const handler = (e: Event) => {
            const tgt = e.target as HTMLElement;
            const wrap = tgt.closest(".mktoFieldWrap") as HTMLElement | null;
            if (wrap) validateField(wrap);
          };
          native.addEventListener(evt, handler, true);
          eventHandlersRef.current.push(() => native.removeEventListener(evt, handler, true));
        });
        form.onValidate(() => {
          const wraps = native.querySelectorAll(".mktoFieldWrap");
          let allValid = true;
          native.querySelectorAll(".customError").forEach(el => el.remove());
          native.querySelectorAll(".mktoInvalid").forEach(el => el.classList.remove("mktoInvalid"));
          wraps.forEach(w => {
            if (!validateField(w as HTMLElement)) allValid = false;
          });
          form.submittable(allValid);
          if (!allValid) {
            const firstInv = native.querySelector(".mktoInvalid") as HTMLElement;
            const rect = firstInv?.getBoundingClientRect();
            if (rect) window.scrollTo({
              top: rect.top + window.scrollY - window.innerHeight / 4,
              behavior: "smooth"
            });
          }
        });
        form.onSuccess(() => {
          if (redirectUrl && hasRedirect) {
            window.location.href = redirectUrl;
          } else {
            if (native) native.style.display = "none";
            document.getElementById("success")?.style.setProperty("display", "block");
          }
          return false;
        });
      });
    }).catch(err => console.error("Failed to load Marketo Forms script:", err));
    return () => {
      mounted = false;
      setIsFormReady(false);
      eventHandlersRef.current.forEach(remove => remove());
      eventHandlersRef.current = [];
      const container = document.getElementById(formContainerId);
      if (container) container.innerHTML = "";
    };
  }, [marketoFormUrl, marketoFormInstanceId, marketoFormId, fullWidth, formContainerId, loadMktoScript, redirectUrl, hasRedirect]);

  // inject custom CSS (from CMS)
  React.useEffect(() => {
    if (!customCss) return;
    const styleEl = document.createElement("style");
    styleRef.current = styleEl;
    styleEl.textContent = cleanCssString(customCss);
    document.head.appendChild(styleEl);
    return () => {
      styleRef.current?.remove();
      styleRef.current = null;
    };
  }, [customCss]);
  if (!marketoFormUrl || !marketoFormInstanceId || !marketoFormId) return null;
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "position": "relative",
    "backgroundColor": "var(--bg-secondary)"
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv>
          <_StyledDiv2 $_css2={[{
          "display": "grid",
          "gridTemplateColumns": "repeat(12, minmax(0, 1fr))"
        }]}>
            {(headline || description) && <_StyledDiv3 $_css3={[{
            "gridColumn": "1 / -1",
            "display": "flex",
            "flexDirection": "column",
            "borderBottomWidth": "1px",
            "borderColor": "var(--stroke-primary)",
            "paddingLeft": "0",
            "paddingRight": "0",
            "paddingTop": "var(--spacing-lg)",
            "paddingBottom": "var(--spacing-lg)",
            "@media (min-width: 1024px)": {
              "gridColumn": "span 4 / span 4",
              "gap": "var(--spacing-lg)",
              "borderWidth": "0px",
              "paddingLeft": "var(--spacing-3xl)",
              "paddingRight": "var(--spacing-3xl)",
              "paddingTop": "var(--spacing-xl)",
              "paddingBottom": "var(--spacing-xl)"
            }
          }]}>
                {headline && <_StyledHeadline type="h2" text={headline} />}
                {description && <_StyledRichText text={description} />}
              </_StyledDiv3>}
            <_StyledDiv4 $_css4={[{
            "gridColumn": "1 / -1",
            "display": "flex",
            "flex": "1 1 0%",
            "flexDirection": "column"
          }, headline || description ? {
            "@media (min-width: 1024px)": {
              "gridColumn": "span 8 / span 8"
            }
          } : {
            "@media (min-width: 1024px)": {
              "gridColumn": "span 12 / span 12"
            }
          }, variants.general]}>
              {!isFormReady && <_StyledDiv5 role="status" aria-live="polite" aria-label="Loading form" $_css5={[{
              "display": "flex",
              "height": "1300px",
              "alignItems": "flex-start",
              "justifyContent": "center",
              "paddingTop": "8rem",
              "@media (min-width: 1024px)": {
                "paddingTop": "12rem"
              }
            }]}>
                  <Icon icon={Loading} size="32" />
                </_StyledDiv5>}
              <_StyledDiv6 id="success" style={{
              display: "none"
            }}>
                <_StyledDiv7>
                  <_StyledH>{successMessageTitle}</_StyledH>
                  {successMessageDescription}
                </_StyledDiv7>
                {successMessageImage}
              </_StyledDiv6>
              <form id={formContainerId} />
            </_StyledDiv4>
          </_StyledDiv2>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-1rnb1di-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-1rnb1di-1"
})({
  "position": "relative",
  "zIndex": "10"
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-1rnb1di-2"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-1rnb1di-3"
})`${p => p.$_css3}`;
var _StyledHeadline = _styled5(Headline).withConfig({
  componentId: "sc-sf-1rnb1di-4"
})({
  "borderBottomWidth": "1px",
  "borderColor": "var(--stroke-primary)",
  "paddingLeft": "var(--spacing-xl)",
  "paddingRight": "var(--spacing-xl)",
  "paddingBottom": "var(--spacing-lg)",
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "borderWidth": "0px",
    "padding": "0",
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledRichText = _styled6(RichText).withConfig({
  componentId: "sc-sf-1rnb1di-5"
})({
  "paddingLeft": "var(--spacing-xl)",
  "paddingRight": "var(--spacing-xl)",
  "paddingTop": "var(--spacing-lg)",
  "@media (min-width: 1024px)": {
    "padding": "0",
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = _styled7("div").withConfig({
  componentId: "sc-sf-1rnb1di-6"
})`${p => p.$_css4}`;
var _StyledDiv5 = _styled8("div").withConfig({
  componentId: "sc-sf-1rnb1di-7"
})`${p => p.$_css5}`;
var _StyledDiv6 = _styled9("div").withConfig({
  componentId: "sc-sf-1rnb1di-8"
})({
  "display": "flex",
  "maxWidth": "65ch",
  "flexDirection": "column"
});
var _StyledDiv7 = _styled10("div").withConfig({
  componentId: "sc-sf-1rnb1di-9"
})({
  "display": "flex",
  "flexDirection": "column"
});
var _StyledH = /*#__PURE__*/_styled11("h2").withConfig({
  displayName: "marketo-embed-form___StyledH",
  componentId: "sc-12o5v3i-0"
})({
  "fontSize": "2.2rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.2rem",
    "lineHeight": "1.2",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});